import { cloneElement, isValidElement, type ReactNode } from 'react'

import { cn } from '../cn'

// FormField — the accessible unit (design/components/input.html §2.3).
// Wraps label + control + helper/error into one node and wires them:
// label↔control via htmlFor/id, message via aria-describedby, error via
// aria-invalid. The control (Input/Textarea/etc.) is the single child; the
// field injects the a11y plumbing so callers never have to remember it.

export interface FormFieldProps {
  label: string
  /** Ties <label> to the control; also injected as the control's id. */
  htmlFor: string
  /** `true` → required (adds * + native required); `'optional'` → "opcional" suffix. */
  required?: boolean | 'optional'
  helper?: string
  /** When set, takes over from helper and turns the field red + aria-invalid. */
  error?: string
  /** When set (and no error), shows a green confirmation message. */
  success?: string
  /** Inline adornment after the label text (e.g. a lock chip). */
  labelSuffix?: ReactNode
  className?: string
  children: ReactNode
}

// the props FormField may inject onto its control child
interface ControlProps {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  required?: boolean
  state?: 'default' | 'error' | 'success'
}

/** Accessible field unit: label + single control child + helper/error/success
 *  line. Clones the child to inject id (forced to `htmlFor`), aria-describedby,
 *  aria-invalid, required and visual `state`, so callers never wire a11y by
 *  hand; `error` beats `success` beats `helper`. */
export function FormField({
  label,
  htmlFor,
  required,
  helper,
  error,
  success,
  labelSuffix,
  className,
  children,
}: FormFieldProps) {
  const msgId = `${htmlFor}-msg`
  const message = error ?? success ?? helper
  const tone = error ? 'error' : success ? 'success' : undefined

  let control: ReactNode = children
  if (isValidElement<ControlProps>(children)) {
    const prev = children.props
    // build overrides without undefined values (exactOptionalPropertyTypes).
    // Force the control id to `htmlFor` so the <label htmlFor> and the message id
    // always match, even if the child carried its own id.
    const overrides: ControlProps = { id: htmlFor }
    const describedby = message
      ? [prev['aria-describedby'], msgId].filter(Boolean).join(' ')
      : prev['aria-describedby']
    if (describedby) overrides['aria-describedby'] = describedby
    if (error) overrides['aria-invalid'] = true
    else if (prev['aria-invalid'] !== undefined) overrides['aria-invalid'] = prev['aria-invalid']
    if (required === true) overrides.required = true
    else if (prev.required !== undefined) overrides.required = prev.required
    // drive the control's visual state from the field's message, unless caller set one
    const nextState = tone ?? prev.state
    if (nextState) overrides.state = nextState
    control = cloneElement(children, overrides)
  }

  return (
    <div className={cn('flex w-full flex-col gap-[5px]', className)}>
      <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-text">
        {label}
        {required === true && (
          <>
            <span aria-hidden className="ml-0.5 text-sens">
              *
            </span>
            <span className="sr-only"> (obligatorio)</span>
          </>
        )}
        {required === 'optional' && <span className="ml-1 font-medium text-text-3">opcional</span>}
        {labelSuffix}
      </label>

      {control}

      {message && (
        <span
          id={msgId}
          className={cn(
            'flex items-center gap-[5px] text-[10.5px]',
            error ? 'text-sens' : success ? 'text-ok' : 'text-text-3',
          )}
        >
          {error && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden
              className="size-[11px] shrink-0"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.5v.01" />
            </svg>
          )}
          {!error && success && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="size-[11px] shrink-0"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
          {message}
        </span>
      )}
    </div>
  )
}
